import React, { useState, useEffect, useCallback } from 'react';
import { MathJax } from "better-react-mathjax";
import { useParams } from "react-router-dom";

import './Solution2.css';

/**
 * Converts the flat solution array into renderable groups:
 *   { type: 'text', value: string }
 *   { type: 'formula', rows: [{ start_step, step, hint }] }
 *
 * Alignment rules:
 *   - start_step        → new row, column 0
 *   - step after start_step → same row, column 1
 *   - step otherwise    → new row, column 0 empty, column 1 filled (keeps vertical alignment)
 *   - hint              → column 2 of current row
 */
function groupElements(elements) {
    const groups = [];
    let formulaRows = null;
    let prevKey = null;

    for (const el of elements) {
        const key = Object.keys(el)[0];

        if (key === 'text') {
            if (formulaRows) {
                groups.push({ type: 'formula', rows: formulaRows });
                formulaRows = null;
            }
            groups.push({ type: 'text', value: el.text });
        } else {
            if (!formulaRows) formulaRows = [];

            if (key === 'start_step') {
                formulaRows.push({ start_step: el.start_step, step: null, hint: null });
            } else if (key === 'step') {
                if (prevKey === 'start_step' && formulaRows.length > 0) {
                    formulaRows[formulaRows.length - 1].step = el.step;
                } else {
                    formulaRows.push({ start_step: null, step: el.step, hint: null });
                }
            } else if (key === 'hint') {
                if (formulaRows.length > 0) {
                    formulaRows[formulaRows.length - 1].hint = el.hint;
                }
            }
        }

        prevKey = key;
    }

    if (formulaRows) {
        groups.push({ type: 'formula', rows: formulaRows });
    }

    return groups;
}

const Solution3 = ({ onClose, index }) => {
    const { title, subTitle, section, id: exercise_id } = useParams();

    const jsonData = require(`../../../data/${title}/${subTitle}.json`);
    const solutionArray = jsonData[subTitle][section][exercise_id - 1]['solution'];

    const [displayed, setDisplayed] = useState(() => solutionArray.slice(0, index));
    const [solutionIndex, setSolutionIndex] = useState(index);

    const previousClick = useCallback(() => {
        if (solutionIndex > 0) {
            setDisplayed(prev => prev.slice(0, -1));
            setSolutionIndex(i => i - 1);
        }
    }, [solutionIndex]);

    const nextClick = useCallback(() => {
        if (solutionIndex < solutionArray.length) {
            setDisplayed(prev => [...prev, solutionArray[solutionIndex]]);
            setSolutionIndex(i => i + 1);
        }
    }, [solutionIndex, solutionArray]);

    const showFullSolution = useCallback(() => {
        setDisplayed(solutionArray);
        setSolutionIndex(solutionArray.length);
    }, [solutionArray]);

    // Retypeset MathJax and scroll down whenever displayed elements change
    useEffect(() => {
        if (window.MathJax) {
            window.MathJax.typesetPromise();
        }
        const solutionDiv = document.getElementById('solution-content');
        if (solutionDiv) {
            solutionDiv.scrollTop = solutionDiv.scrollHeight;
        }
    }, [displayed]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.keyCode === 39) nextClick();
            else if (event.keyCode === 37) previousClick();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [nextClick, previousClick]);

    const groups = groupElements(displayed);

    return (
        <div className='solution-div'>
            <div id="solution" className="solution">
                <button id="closeSolution" className="close-button" onClick={() => onClose(solutionIndex)}>
                    &times;
                </button>
                <div className="button-container">
                    <button id="previous" className="nav-button" onClick={previousClick}>Previous</button>
                    <button id="next" className="nav-button" onClick={nextClick}>Next</button>
                    <button id="showFullSolution" className="nav-button" onClick={showFullSolution}>Show Full Solution</button>
                </div>
                <div className='solution-content' id="solution-content">
                    <MathJax dynamic>
                        <div style={{ textAlign: 'left' }} id='solution-formulas'>
                            {groups.map((group, i) =>
                                group.type === 'text'
                                    ? <p key={i}>{group.value}</p>
                                    : <table key={i}>
                                        <tbody>
                                            {group.rows.map((row, j) => (
                                                <tr key={j}>
                                                    <td>{row.start_step}</td>
                                                    <td>{row.step}</td>
                                                    <td>{row.hint}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                            )}
                        </div>
                    </MathJax>
                </div>
            </div>
        </div>
    );
};

export default Solution3;
